import { Link } from "react-router-dom";
import { useEffect } from "react";
import { useCart } from "@/hooks/useCart";

export default function CartDrawer() {
  const cart = useCart();

  useEffect(() => {
    if (!cart.isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [cart.isOpen]);

  return (
    <div
      aria-hidden={!cart.isOpen}
      className={`fixed inset-0 z-[70] ${cart.isOpen ? "pointer-events-auto" : "pointer-events-none"}`}
    >
      <div
        onClick={cart.close}
        className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${cart.isOpen ? "opacity-100" : "opacity-0"}`}
      />
      <aside
        className={`absolute top-0 right-0 h-full w-full sm:w-[420px] bg-white text-neutral-800 flex flex-col transform transition-transform duration-300 ease-out ${
          cart.isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 h-16 border-b border-neutral-200">
          <p className="text-[11px] tracking-[0.24em] uppercase text-neutral-500">Your Bag ({cart.count})</p>
          <button onClick={cart.close} className="w-8 h-8 flex items-center justify-center hover:text-neutral-900 cursor-pointer" aria-label="Close bag">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {cart.items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-4">
              <i className="ri-shopping-bag-line text-3xl text-neutral-400"></i>
              <p className="text-[13px] text-neutral-600">Your bag is empty.</p>
              <Link
                to="/shop"
                onClick={cart.close}
                className="text-[12px] tracking-[0.15em] uppercase text-neutral-900 border-b border-neutral-400 pb-1 hover:text-primary-600 cursor-pointer"
              >
                Start shopping
              </Link>
            </div>
          ) : (
            <ul className="space-y-6">
              {cart.items.map((it) => {
                const key = cart.keyOf(it);
                return (
                  <li key={key} className="flex gap-4">
                    <div className="w-20 h-24 bg-neutral-50 overflow-hidden flex-shrink-0">
                      <img src={it.img} alt={it.name} className="w-full h-full object-contain object-center" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="text-[13px] text-neutral-900 truncate">{it.name}</h3>
                          <p className="text-[11px] text-neutral-500 mt-1">
                            {it.color}{it.size ? ` · ${it.size}` : ""}
                          </p>
                        </div>
                        <span className="text-[13px] text-neutral-900 whitespace-nowrap">${it.price * it.quantity}</span>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center border border-neutral-200 rounded-full">
                          <button
                            onClick={() => cart.updateQty(key, it.quantity - 1)}
                            className="w-7 h-7 flex items-center justify-center text-neutral-600 hover:text-neutral-900 cursor-pointer"
                            aria-label="Decrease"
                          >
                            <i className="ri-subtract-line text-sm"></i>
                          </button>
                          <span className="w-6 text-center text-[12px] text-neutral-900">{it.quantity}</span>
                          <button
                            onClick={() => cart.updateQty(key, it.quantity + 1)}
                            className="w-7 h-7 flex items-center justify-center text-neutral-600 hover:text-neutral-900 cursor-pointer"
                            aria-label="Increase"
                          >
                            <i className="ri-add-line text-sm"></i>
                          </button>
                        </div>
                        <button
                          onClick={() => cart.remove(key)}
                          className="text-[11px] text-neutral-500 hover:text-primary-600 cursor-pointer"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {cart.items.length > 0 && (
          <div className="border-t border-neutral-200 px-6 py-5 space-y-4">
            <div className="flex items-center justify-between text-[13px] text-neutral-900">
              <span>Subtotal</span>
              <span>${cart.subtotal}</span>
            </div>
            <p className="text-[11px] text-neutral-500">Shipping and taxes calculated at checkout.</p>
            <div className="flex flex-col gap-2">
              <Link
                to="/checkout"
                onClick={cart.close}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-primary-500 hover:bg-primary-600 text-white text-[13px] rounded-full transition cursor-pointer whitespace-nowrap"
              >
                Checkout <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/cart"
                onClick={cart.close}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-[12px] tracking-[0.15em] uppercase text-neutral-900 border border-neutral-200 rounded-full hover:border-neutral-400 cursor-pointer whitespace-nowrap"
              >
                View Cart
              </Link>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}